// EmployeeTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./EmployeeTable.scss";
import axiosInstance from "../../axiosInstace";
import { useAuth } from "../../contexts/AuthContext"; // <<-- read auth user

const ROLES = [
  { id: 1, name: "APPROVER" },
  { id: 2, name: "CREATOR" },
  { id: 3, name: "DOER" },
  { id: 4, name: "VIEWER" },
  { id: 5, name: "CLIENT" },
];

// fallback demo employees (5)
const DUMMY_EMPLOYEES = [
  {
    empId: 9001,
    empName: "Demo User 1",
    departmentName: "Engineering",
    roleName: "Frontend Developer",
    email: "demo1@example.com",
  },
  {
    empId: 9002,
    empName: "Demo User 2",
    departmentName: "Engineering",
    roleName: "Backend Developer",
    email: "demo2@example.com",
  },
  {
    empId: 9003,
    empName: "Demo User 3",
    departmentName: "Design",
    roleName: "UI/UX Designer",
    email: "demo3@example.com",
  },
  {
    empId: 9004,
    empName: "Demo User 4",
    departmentName: "QA",
    roleName: "QA Engineer",
    email: "demo4@example.com",
  },
  {
    empId: 9005,
    empName: "Demo User 5",
    departmentName: "Management",
    roleName: "Project Manager",
    email: "demo5@example.com",
  },
];

export default function EmployeeTable({
  onAdd /* receives members array */,
  creatorEmpId: propCreatorEmpId = null,
}) {
  const BACKEND_BASE =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:8082";
  const { user, isLoading: authLoading } = useAuth(); // auth user + loading state

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedRoles, setSelectedRoles] = useState({}); // empId -> roleIdString
  const [addedMap, setAddedMap] = useState({}); // empId -> { empId, empName, roleId }

  // dynamic roles fetched from backend
  const [rolesList, setRolesList] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");

  const ROLE_OPTIONS = (rolesList && rolesList.length ? rolesList : ROLES).map(
    (r) => ({ value: String(r.id), label: r.name }),
  );

  // Prefer empId from AuthContext user, otherwise allow prop fallback
  const creatorEmpId = user?.empId ?? propCreatorEmpId ?? null;
  const creatorName =
    user?.employeeName ?? user?.empName ?? user?.username ?? "";

  // fetch employees
  useEffect(() => {
    let mounted = true;
    async function fetchEmployees() {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`${BACKEND_BASE}/api/emp/getall`);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (!mounted) return;
        setEmployees(
          list.map((e) => ({
            empId: e.empId,
            empName: e.empName,
            departmentName: e.departmentName || e.department || "",
            roleName: e.roleName || e.role || "",
            email: e.email || "",
            mobileNumber: e.mobileNumber || "",
          })),
        );
        setFetchError("");
      } catch (err) {
        console.error("Failed to fetch employees", err);
        if (!mounted) return;
        setFetchError("Failed to load employees — showing demo employees");
        setEmployees(DUMMY_EMPLOYEES.slice(0, 5));
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }
    fetchEmployees();
    return () => {
      mounted = false;
    };
  }, [BACKEND_BASE]);

  // fetch roles from backend
  useEffect(() => {
    let mounted = true;
    async function fetchRoles() {
      setRolesLoading(true);
      try {
        const res = await axiosInstance.get(
          `${BACKEND_BASE}/api/emp/role/getall`,
        );
        // expected shape per your example: { status:200, message:..., data: [ { roleId, roleName, ... }, ... ] }
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        if (!mounted) return;
        setRolesList(
          list.map((r) => ({
            id: r.roleId ?? r.id ?? null,
            name:
              r.roleName ??
              r.roleType ??
              r.role ??
              String(r.roleId ?? r.id ?? r),
          })),
        );
        setRolesError("");
      } catch (err) {
        console.error("Failed to fetch roles", err);
        if (!mounted) return;
        setRolesError("Failed to load roles — using defaults");
        setRolesList(ROLES.map((r) => ({ id: r.id, name: r.name })));
      } finally {
        if (!mounted) return;
        setRolesLoading(false);
      }
    }
    fetchRoles();
    return () => {
      mounted = false;
    };
  }, [BACKEND_BASE]);

  // auto-add creator as CREATOR if provided
  useEffect(() => {
    if (!creatorEmpId) return;

    // find in fetched employees first
    const found = employees.find(
      (e) => String(e.empId) === String(creatorEmpId),
    );

    // prepare creator object (either found in employees or built from auth user)
    const creatorObj = found ?? {
      empId: creatorEmpId,
      empName: creatorName || `User ${creatorEmpId}`,
      departmentName: user?.departmentName ?? "—",
      roleName: user?.role ?? "—",
      email: user?.email ?? user?.username ?? "",
    };

    // if already added, do nothing
    if (addedMap && addedMap[String(creatorObj.empId)]) return;

    // pick creator id from fetched roles if available, else fallback
    const creatorRoleEntry = rolesList.find(
      (r) => String(r.name).toLowerCase() === "creator",
    );
    const roleId =
      (creatorRoleEntry && creatorRoleEntry.id) ??
      ROLES.find((r) => r.name === "CREATOR")?.id ??
      2;
    const roleIdStr = String(roleId);

    setSelectedRoles((prev) => ({ ...prev, [creatorObj.empId]: roleIdStr }));
    setAddedMap((prev) => {
      const next = {
        ...prev,
        [creatorObj.empId]: {
          empId: creatorObj.empId,
          empName: creatorObj.empName,
          roleId: Number(roleIdStr),
        },
      };
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorEmpId, employees, rolesList]);

  // new handleRoleChange: accepts emp object and roleIdString
  const handleRoleChange = (emp, roleIdString) => {
    if (!emp) return;

    // Lock creator (do not allow role change/removal)
    if (creatorEmpId && String(emp.empId) === String(creatorEmpId)) return;

    const empIdKey = String(emp.empId);
    const prevSelected = selectedRoles[empIdKey];

    // If clicking the same pill again -> toggle off (remove role + uncheck)
    if (prevSelected && String(prevSelected) === String(roleIdString)) {
      // remove selected role
      setSelectedRoles((prev) => {
        const next = { ...prev };
        delete next[empIdKey];
        return next;
      });

      // remove from addedMap
      setAddedMap((prev) => {
        if (!prev[emp.empId]) return prev;
        const next = { ...prev };
        delete next[emp.empId];
        return next;
      });

      return;
    }

    // Otherwise set selected role
    setSelectedRoles((prev) => ({ ...prev, [empIdKey]: String(roleIdString) }));

    // Add to addedMap if not present, otherwise update roleId in-place
    setAddedMap((prev) => {
      const next = { ...prev };
      if (next[emp.empId]) {
        next[emp.empId] = { ...next[emp.empId], roleId: Number(roleIdString) };
      } else {
        next[emp.empId] = {
          empId: emp.empId,
          empName: emp.empName || emp.empName || `User ${emp.empId}`,
          roleId: Number(roleIdString),
        };
      }
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!search) return employees;
    const q = search.toLowerCase();
    return employees.filter(
      (e) =>
        (e.empName || "").toLowerCase().includes(q) ||
        (e.roleName || "").toLowerCase().includes(q),
    );
  }, [employees, search]);

  // filter out creator from the listing so they can't be added twice
  const renderList = useMemo(() => {
    if (!filtered) return [];
    if (!creatorEmpId) return filtered;
    return filtered.filter((e) => String(e.empId) !== String(creatorEmpId));
  }, [filtered, creatorEmpId]);

  // Generate first letter of name
  const getInitial = (name = "") => {
    return name?.trim()?.charAt(0)?.toUpperCase() || "U";
  };

  // Pick a consistent background color for each user
  const getColorForName = (name = "") => {
    const colors = [
      "#2563EB", // blue
      "#10B981", // green
      "#F59E0B", // amber
      "#EF4444", // red
      "#8B5CF6", // violet
      "#EC4899", // pink
      "#14B8A6", // teal
      "#F97316", // orange
    ];
    const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // build a small array of added members enriched with role name + department
  const addedMembers = useMemo(() => {
    return Object.values(addedMap).map((m) => {
      const emp =
        employees.find((e) => String(e.empId) === String(m.empId)) || {};
      const roleName =
        rolesList.find((r) => Number(r.id) === Number(m.roleId))?.name ||
        ROLES.find((r) => Number(r.id) === Number(m.roleId))?.name ||
        String(m.roleId);
      return {
        empId: m.empId,
        empName: m.empName,
        departmentName:
          emp.departmentName ||
          emp.department ||
          (String(m.empId) === String(creatorEmpId)
            ? (user?.departmentName ?? "—")
            : "—"),
        roleName,
      };
    });
  }, [addedMap, employees, creatorEmpId, user, rolesList]);

  // ---------- replace existing useEffect that calls onAdd ----------
  useEffect(() => {
    if (typeof onAdd !== "function") return;

    const timer = setTimeout(() => {
      // Build payload to parent: include roleName to avoid id mismatches
      const raw = Object.values(addedMap);

      // Optionally exclude creator (keep legacy behaviour)
      const filtered = raw.filter(
        (m) => String(m.empId) !== String(creatorEmpId),
      );

      const enriched = (filtered || []).map((m) => {
        // try to resolve roleName from rolesList; fall back to ROLES constant
        const roleEntry =
          (rolesList || []).find((r) => Number(r.id) === Number(m.roleId)) ||
          ROLES.find((r) => Number(r.id) === Number(m.roleId)) ||
          null;

        const roleName =
          (roleEntry && String(roleEntry.name).toUpperCase()) ||
          (m.roleId != null ? String(m.roleId) : "");

        return {
          empId: Number(m.empId),
          empName: m.empName,
          roleId: Number(m.roleId),
          roleName,
        };
      });

      // debug: show what EmployeeTable sends to parent
      // eslint-disable-next-line no-console
      console.debug("[EmployeeTable] onAdd payload:", enriched);

      onAdd(enriched);
    }, 60);

    return () => clearTimeout(timer);
  }, [addedMap, onAdd, creatorEmpId, rolesList]);

  return (
    <div className="employee-table-wrapper">
      <input
        type="text"
        className="search-bar"
        placeholder="Search by name or role..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="table-container">
        <table className="employee-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Current Role</th>
              <th>Assign Role</th>
            </tr>
          </thead>
          <tbody>
            {loading || authLoading ? (
              <tr>
                <td colSpan={5}>Loading employees...</td>
              </tr>
            ) : renderList.length === 0 ? (
              <tr>
                <td colSpan={5}>No employees found.</td>
              </tr>
            ) : (
              renderList.map((emp) => (
                <tr key={emp.empId}>
                  <td className="employee-info">
                    {/* Profile symbol (initials) instead of random fetched avatar */}
                    <div className="profile-symbol" aria-hidden>
                      <div
                        className="emp-initial-avatar"
                        style={{
                          backgroundColor: getColorForName(emp.empName),
                        }}
                      >
                        {getInitial(emp.empName)}
                      </div>
                    </div>

                    <div className="emp-details">
                      <span className="emp-name">{emp.empName}</span>
                    </div>
                  </td>
                  <td>{emp.departmentName}</td>
                  <td>{emp.roleName}</td>
                  <td style={{ minWidth: 180 }}>
                    {/* Role pills */}
                    <div className="role-pill-group">
                      {(ROLE_OPTIONS || []).map((opt) => {
                        const isSelected =
                          selectedRoles[String(emp.empId)] ===
                          String(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`role-pill ${isSelected ? "selected" : ""}`}
                            onClick={() =>
                              handleRoleChange(emp, String(opt.value))
                            }
                            disabled={rolesLoading}
                            aria-pressed={isSelected}
                            title={opt.label}
                          >
                            {/* Display Title Case label; payload still uses roleId */}
                            {String(opt.label)
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {fetchError && (
        <div style={{ marginTop: 8, color: "#a00" }}>{fetchError}</div>
      )}
      {rolesError && (
        <div style={{ marginTop: 8, color: "#a00" }}>{rolesError}</div>
      )}

      {/* --- Added members compact table --- */}
      <div style={{ marginTop: 18 }}>
        <h3 style={{ marginBottom: 10 }}>Added Members</h3>

        {addedMembers.length === 0 ? (
          <div style={{ color: "#666" }}>No members added yet.</div>
        ) : (
          <div
            className="added-members-table-wrapper"
            style={{ overflowX: "auto" }}
          >
            <table
              className="added-members-table"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 10px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    Role
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 10px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 10px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    Department
                  </th>
                </tr>
              </thead>
              <tbody>
                {addedMembers.map((m) => (
                  <tr
                    key={m.empId}
                    style={{ borderBottom: "1px solid #fafafa" }}
                  >
                    <td style={{ padding: 10 }}>
                      {m.roleName}
                      {String(m.empId) === String(creatorEmpId)
                        ? " (Creator)"
                        : ""}
                    </td>
                    <td style={{ padding: 10 }}>
                      {m.empName}
                      {String(m.empId) === String(creatorEmpId) ? " (You)" : ""}
                    </td>
                    <td style={{ padding: 10 }}>{m.departmentName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
